import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Reveal } from '../Reveal';
import { PillButton } from '../PillButton';
import { ProductVisual } from '../products/ProductVisual';
import { SiblingGuessRow } from '../SiblingGuessRow';
import { SiblingFigure } from '../characters/SiblingFigure';
import { getSibling } from '../../data/siblings';
import type { FilteknProduct, SiblingRole } from '../../types/snak';
interface ProductScreenProps {
  product: FilteknProduct;
  position: number;
  total: number;
  onNext: () => void;
}
export function ProductScreen({
  product,
  position,
  total,
  onNext
}: ProductScreenProps) {
  const [guess, setGuess] = useState<SiblingRole | null>(null);
  const sibling = getSibling(product.answer);
  const matched = guess === product.answer;
  return <div className="flex flex-1 flex-col py-3" data-id="element-173" data-mp="y8vlnvd8ttit">
      <Reveal data-id="element-174" data-mp="y5znc5xhh2tu">
        <span className="font-body text-[10px] uppercase tracking-[0.22em] text-primary" data-id="element-175" data-mp="129ntqladpsdi">
          Sibling {position} of {total}
        </span>
      </Reveal>
      <Reveal delay={0.06} data-id="element-176" data-mp="q40msvf5clqy">
        <h2 className="mt-2 font-heading text-[32px] leading-[1] tracking-[-0.02em] text-white" data-id="element-177" data-mp="q030y216pg3d">
          {product.headline}
        </h2>
      </Reveal>

      <Reveal delay={0.14} className="mt-5" data-id="element-178" data-mp="g5b5p4v2r5br">
        <div className="relative overflow-hidden rounded-[26px] bg-white px-5 py-4 shadow-[0_24px_60px_-32px_rgba(0,0,0,0.65)]" data-id="element-179" data-mp="g62y66tdkmxt">
          {/* subtle family-role cue behind the product */}
          <AnimatePresence data-id="element-180" data-mp="18v02t74nyl83">
            {guess && <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 0.1,
            scale: 1
          }} transition={{
            duration: 0.7
          }} className="pointer-events-none absolute -right-4 bottom-0 top-2 flex items-center" data-id="element-181" data-mp="13uvlxfzufohc">
                <SiblingFigure role={product.answer} className="h-[150px] w-auto" data-id="element-182" data-mp="1rjopnympj0mi" />
              </motion.div>}
          </AnimatePresence>

          <div className="relative flex items-center gap-4" data-id="element-183" data-mp="1mqvht1vxzc67">
            <ProductVisual product={product.id} className="h-[176px] w-auto shrink-0" data-id="element-184" data-mp="3h1va3ipim402" />
            <ul className="flex flex-col gap-2" data-id="element-185" data-mp="3lylntzduknib">
              {product.traits.map((trait, i) => <motion.li key={trait} initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3 + i * 0.1,
              duration: 0.45
            }} className="rounded-full bg-[rgba(1,51,43,0.06)] px-3 py-1.5 font-body text-[12px] leading-tight text-background" data-id="element-186" data-mp="379pl8huk82p3" data-mpi={i}>
                  {trait}
                </motion.li>)}
            </ul>
          </div>
        </div>
      </Reveal>

      <AnimatePresence mode="wait" data-id="element-187" data-mp="2tbr5p94i6080">
        {!guess ? <motion.div key="ask" exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.3
      }} className="mt-6" data-id="element-188" data-mp="mkrqoji06s7d">
            <p className="font-heading text-[19px] text-white" data-id="element-189" data-mp="rhig3xfp705g">{product.question}</p>
            <div className="mt-3" data-id="element-190" data-mp="2c25hm4av52kq">
              <SiblingGuessRow guess={guess} onGuess={setGuess} data-id="element-191" data-mp="272khvrnvcq6y" />
            </div>
          </motion.div> : <motion.div key="reveal" initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55,
        delay: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-6" data-id="element-192" data-mp="2lrysf336ldnx">
            <p className="font-body text-[13px] text-white/50" data-id="element-193" data-mp="2uu7z1hvbwmw1">
              {matched ? 'Same instinct.' : 'Interesting call.'} We were thinking...
            </p>
            <div className="mt-2 flex items-center gap-3" data-id="element-194" data-mp="34ozxsp0ndfse">
              <motion.span initial={{
            scale: 0.7,
            rotate: -8
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 16,
            delay: 0.25
          }} className="flex h-14 w-14 shrink-0 items-end justify-center overflow-hidden rounded-full" style={{
            backgroundColor: sibling.surface
          }} data-id="element-195" data-mp="2zrhlqprc2kg6">
                <SiblingFigure role={product.answer} className="h-[54px] w-auto" data-id="element-196" data-mp="1f3af0xdo5lq" />
              </motion.span>
              <h3 className="font-heading text-[30px] leading-none tracking-[-0.02em] text-primary" data-id="element-197" data-mp="6dwjmh5mtag3">
                {product.answerLabel}.
              </h3>
            </div>
            <p className="mt-3 font-body text-[15px] leading-relaxed text-white/75" data-id="element-198" data-mp="39v5478rquoei">
              {product.supporting}
            </p>
            <p className="mt-4 border-t border-white/[0.12] pt-4 font-body text-[13px] leading-relaxed text-white/55" data-id="element-199" data-mp="3evaykwpeep0a">
              {product.closing}
            </p>
            <div className="mt-5" data-id="element-200" data-mp="3nmb7zh223877">
              <PillButton tone="dark" full onClick={onNext} data-id="element-201" data-mp="3nnl6g7ctmied">
                Next sibling
              </PillButton>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}